/* eslint-disable max-len */
import { Alert, AlertSeverity, AlertState } from '@moderepo/mode-internal-apis';
import { capitalizeAll, GenericMessageBox, useModeTableStyle } from '@moderepo/mode-app-components';
import { Paper, Theme, makeStyles, CircularProgress, Typography, Table, TableHead, TableCell, TableRow, TableBody, Icon } from '@material-ui/core';
import clsx from 'clsx';
import { CSSProperties, useCallback, useContext, useMemo, useState } from 'react';
import { UIFetchAlertsFilters } from '@moderepo/mode-data-state-user-app';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useSelectAlerts, useSelectEntity, useSelectProject } from '../../utils/dataStateHooks';
import { useRitzTranslation } from '../../utils/customHooks';
import { AlertPreviewCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';
import { getDateTimeDisplay } from '../containerUtils';
import { getAlertSeverityDisplayName, getAlertStateDisplayName } from '..';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { getEntityHealthConditionDisplayName } from '.';


const useStyle = makeStyles((theme: Theme) => {
    return {
        root: {
        },

        statusHeader: {
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'space-between',
            padding       : theme.spacing(1),
        },

        statusHeaderTitle: {
        },

        healthInfo: {
            display      : 'flex',
            flexDirection: 'row',
            alignItems   : 'center',
        },

        healthCondition: {
            display             : 'flex',
            flexDirection       : 'column',
            justifyContent      : 'center',
            alignItems          : 'flex-end',
            textAlign           : 'right',
            '& .condition-label': {

            },
            '& .condition-value': {
                color                              : theme.palette.success.main,
                [`&.severity-${AlertSeverity.LOW}`]: {
                    color: theme.palette.info.main,
                },
                [`&.severity-${AlertSeverity.MODERATE}`]: {
                    color: theme.palette.warning.main,
                },
                [`&.severity-${AlertSeverity.HIGH}`]: {
                    color: theme.palette.error.main,
                },
            },
        },

        healthIcon: {
            marginLeft    : theme.spacing(1),
            width         : 50,
            height        : 50,
            fontSize      : 30,
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            color         : theme.palette.success.contrastText,
            background    : theme.palette.success.main,
            borderRadius  : '100%',

            [`&.severity-${AlertSeverity.LOW}`]: {
                color     : theme.palette.info.contrastText,
                background: theme.palette.info.main,
            },
            [`&.severity-${AlertSeverity.MODERATE}`]: {
                color     : theme.palette.warning.contrastText,
                background: theme.palette.warning.main,
            },
            [`&.severity-${AlertSeverity.HIGH}`]: {
                color     : theme.palette.error.contrastText,
                background: theme.palette.error.main,
            },
        },


        alertTableContainer: {
            overflow: 'auto',
        },


        alertsTable: {
            borderTop: `1px solid ${theme.palette.divider}`,
        },


        tableCol: {
            textAlign    : 'left',
            '&.state-col': {
                textAlign : 'center',
                '& .value': {
                    fontSize     : 'smaller',
                    fontWeight   : 'bold',
                    padding      : theme.spacing(0.3, 1),
                    borderRadius : 100,
                    textTransform: 'lowercase',
                    background   : theme.palette.info.contrastText,
                    color        : theme.palette.info.contrastText,
                    width        : 'fit-content',
                    minWidth     : '5em',
                    display      : 'inline-block',
                },

                [`&.state-${AlertState.ALERTING}`]: {
                    '& .value': {
                        color     : theme.palette.error.contrastText,
                        background: theme.palette.error.main,
                    },
                },

                [`&.state-${AlertState.INEFFECTIVE}`]: {
                    '& .value': {
                        color     : theme.palette.info.contrastText,
                        background: theme.palette.info.main,
                    },
                },

                [`&.state-${AlertState.OBSOLETE}`]: {
                    '& .value': {
                        color     : theme.palette.info.contrastText,
                        background: theme.palette.info.main,
                    },
                },

                [`&.state-${AlertState.RECOVERED}`]: {
                    '& .value': {
                        color     : theme.palette.success.contrastText,
                        background: theme.palette.success.main,
                    },
                },
            },
            '&.severity-col': {
                textAlign: 'center',

                '& .value': {
                    fontSize     : 'smaller',
                    fontWeight   : 'bold',
                    padding      : theme.spacing(0.3, 1),
                    borderRadius : 100,
                    textTransform: 'lowercase',
                    background   : theme.palette.info.main,
                    color        : theme.palette.info.contrastText,
                    width        : 'fit-content',
                    minWidth     : '5em',
                    display      : 'inline-block',
                },

                [`&.severity-${AlertSeverity.LOW}`]: {
                    '& .value': {
                        color     : theme.palette.info.contrastText,
                        background: theme.palette.info.main,
                    },
                },

                [`&.severity-${AlertSeverity.MODERATE}`]: {
                    '& .value': {
                        color     : theme.palette.warning.contrastText,
                        background: theme.palette.warning.main,
                    },
                },

                [`&.severity-${AlertSeverity.HIGH}`]: {
                    '& .value': {
                        color     : theme.palette.error.contrastText,
                        background: theme.palette.error.main,
                    },
                },
            },
            '&.description-col': {
                width: '100%',
            },
        },


        [theme.breakpoints.up('md')]: {
            statusHeader: {
                padding: theme.spacing(2),
            },
        },
    };
}, { name: 'EntityHealthStatusContainer', index: 1 });



export interface EntityAlertStatusContainerProps {
    readonly projectId: number;
    readonly entityId: string;
    readonly className?: string | undefined;
    readonly style?: CSSProperties | undefined;
    readonly maxAlerts?: number | undefined;

    // To allow the parent container to handle onShowEntityDetails event instead of this component
    readonly onShowEntityDetailsHandler?: ((entityId: string)=> void) | undefined;
}


/**
 * This component is used for showing an entity's health condition and active alerts
 */
export const EntityHealthStatusContainer: React.FC<EntityAlertStatusContainerProps> = (props: EntityAlertStatusContainerProps) => {

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);
    const tableClasses = useModeTableStyle();
    const classes = useStyle();

    // Get the entity's alerts that are in ALERTING state
    const alertFilters = useMemo((): UIFetchAlertsFilters => {
        return {
            entityId  : props.entityId,
            pageNumber: 0,
            pageSize  : props.maxAlerts ?? 100,
            state     : AlertState.ALERTING,
        };
    }, [props.entityId, props.maxAlerts]);
    const alerts = useSelectAlerts(props.projectId, alertFilters);

    // Sort the alerts by highest severity level first
    const sortedAlerts = useMemo((): readonly Alert[] | undefined => {
        if (alerts?.items) {
            return [...alerts.items].sort((alert1, alert2) => {
                // NOTE: Severity levels are numerical BUT their values are in opposite order. The highest severity level
                // value is smaller than the lower severity level.
                return alert1.severity - alert2.severity;
            });
        }
        return undefined;
    }, [alerts?.items]);


    /**
     * If there are 1 or more alerts, use the FIRST alert's severity as the entity's severity level. The alerts are already sorted
     * by severity level so we can assume the first alerts is the one with the highest severity
     */
    const entitySeverity = useMemo((): AlertSeverity | undefined => {
        if (sortedAlerts && sortedAlerts.length > 0) {
            return sortedAlerts[0].severity;
        }
        return undefined;
    }, [sortedAlerts]);


    const [selectedAlert, setSelectedAlert] = useState<Alert>();


    /**
     * On Alert clicked, show the slide out panel to show the alert details
     */
    const onAlertClick = useCallback((alert: Alert) => {
        setSelectedAlert(alert);

        // Dispatch an action to show the Preview Panel for the selected alert.
        const options: AlertPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : SlideOutPanelCompType.ALERT_PREVIEW,
            compProps: {
                data: {
                    projectId: props.projectId,
                    alertId  : alert.id,
                },
                onShowEntityDetailsHandler: props.onShowEntityDetailsHandler,
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.onShowEntityDetailsHandler, props.projectId, uiDispatch]);


    return (
        <>
            {selectedEntity && (
                <Paper elevation={2} className={clsx(props.className, classes.root)} style={props.style}>
                    <div className={clsx(classes.statusHeader)}>
                        <Typography
                            className={classes.statusHeaderTitle}
                            variant="h5"
                            component="h2"
                        >
                            {trans('entity.health.label.entity_status', {
                                entity_name: selectedEntity.displaySettings.name,
                            })}
                        </Typography>
                        <div className={classes.healthInfo}>
                            <div className={classes.healthCondition}>
                                <Typography
                                    className="condition-label"
                                    component="p"
                                >
                                    {capitalizeAll(trans('entity.health.label.condition', {
                                        entity_name: selectedEntity.displaySettings.name,
                                    }))}
                                </Typography>
                                <Typography
                                    className={clsx('condition-value', entitySeverity && `severity-${entitySeverity}`)}
                                    variant="h5"
                                    component="h2"
                                >
                                    {capitalizeAll(trans(getEntityHealthConditionDisplayName(entitySeverity)))}
                                </Typography>
                            </div>
                            <Icon className={clsx(classes.healthIcon, entitySeverity && `severity-${entitySeverity}`)}>
                                local_hospital
                            </Icon>
                        </div>
                    </div>

                    {/**
                     * If there are alerts, show a table of alerts
                     */}
                    {sortedAlerts && sortedAlerts.length > 0 ? (
                        <div className={classes.alertTableContainer}>
                            <Table className={clsx(tableClasses.root, classes.alertsTable)} size="medium">
                                <TableHead className={tableClasses.tableHeader}>
                                    <TableRow className={tableClasses.tableHeaderRow}>
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'description-col')}>
                                            {capitalizeAll(trans('alerting.label.alert', { count: 2 }))}
                                        </TableCell>
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'invocation-date-col')}>
                                            {capitalizeAll(trans('alerting.label.invocation_date'))}
                                        </TableCell>
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'severity-col')}>
                                            {capitalizeAll(trans('label.severity'))}
                                        </TableCell>
                                        <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'state-col')}>
                                            {capitalizeAll(trans('label.state'))}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedAlerts.map((alert): JSX.Element => {
                                        return (
                                            <TableRow
                                                className={clsx(
                                                    tableClasses.tableBodyRow, 'clickable', selectedAlert?.id === alert.id && 'selected',
                                                )}
                                                key={alert.id}
                                                onClick={() => {
                                                    onAlertClick(alert);
                                                }}
                                            >
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'description-col')}>
                                                    {alert.description}
                                                </TableCell>
                                                <TableCell className={clsx(tableClasses.tableCol, classes.tableCol, 'invocation-date-col')}>
                                                    {getDateTimeDisplay(alert.invocationTime, trans)}
                                                </TableCell>
                                                <TableCell
                                                    className={clsx(tableClasses.tableCol, classes.tableCol, 'severity-col',
                                                        `severity-${alert.severity}`)}
                                                >
                                                    <div className="value">
                                                        {trans(getAlertSeverityDisplayName(alert.severity))}
                                                    </div>
                                                </TableCell>
                                                <TableCell
                                                    className={clsx(tableClasses.tableCol, classes.tableCol, 'state-col',
                                                        `state-${alert.state}`)}
                                                >
                                                    <div className="value">
                                                        {trans(getAlertStateDisplayName(alert.state))}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    ) : (
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('alerting.label.alert', { count: 2 })),
                            })}
                        />
                    )}
                </Paper>
            )}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityAlertStatusContainer', EntityHealthStatusContainer,
);
